/**
 * 監視画面用モジュール
 * @module app/view/page/MonitoringPage
 */
define([
    'module',
    'dojo/_base/array',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/aspect',
    'dojo/debounce',
    'dojo/dom-class',
    'dojo/dom-style',
    'dojo/promise/all',
    'dojo/text!./templates/MonitoringPage.html',
    'dojo/on',
    'dojo/json',
    'dojo/topic',
    'dojo/when',
    'idis/consts/STORAGE_KEY',
    'idis/consts/QUERY',
    'idis/control/Locator',
    'idis/map/IdisMap',
    'idis/model/UserInfo',
    'idis/service/Requester',
    'idis/util/storage/LocalStorage',
    'idis/view/dialog/ConfirmDialog',
    'idis/view/dialog/IdisDialog',
    'idis/view/dialog/InfoDialog',
    'idis/view/page/_PageBase',
    'idis/view/form/MunicipalitySelectForm',
    '../../config',
    'app/map/LayerPane',
    'app/map/detail/DetailMap',
    'app/map/baselayer/BaseLayerPane',
    'app/map/legend/LegendPane',
    'app/map/legend/GridLegendPane',
    'app/model/LayerStore',
    // 以下、変数で受けないモジュール
    'dijit/layout/BorderContainer',
    'dijit/layout/TabContainer',
    'dijit/layout/ContentPane',
    'dijit/layout/StackContainer',
    'app/map/AddressPanel',
    'app/map/islandPanel/IslandPanel',
    'app/map/TimestampsPanel',
], function (module, array, declare, lang,
    aspect, debounce, domClass, domStyle, all, template, on, json, topic,
    when, STORAGE_KEY, QUERY, Locator, IdisMap, UserInfo, Requester, LocalStorage,
    ConfirmDialog, IdisDialog, InfoDialog, _PageBase, MunicipalitySelectForm,
    config, LayerPane, DetailMap, BaseLayerPane, LegendPane, GridLegendPane, LayerStore) {
    /**
     * 左側メニュー内の各画面のキー名とコンストラクター関数の対応
     * @type {Object<string,function>}
     */
    var stackClassMap = {
        layer: LayerPane
    };

    return declare(module.id.replace(/\//g, '.'), _PageBase, {
        // 基本クラス
        baseClass: 'idis-Page idis-Page--map',

        // テンプレート文字列
        templateString: template,

        /**
         * 背景地図ダイアログに対する参照
         * @type {module:idis/view/dialog/IdisDialog~IdisDialog}
         * @private
         */
        _baseLayerDialog: null,

        /**
         * 市町村選択ダイアログに対する参照
         * @type {module:idis/view/dialog/IdisDialog~IdisDialog}
         * @private
         */
        _municipalitySelectDialog: null,

        /**
         * メッセージ表示用ダイアログ
         */
        infoDialog : null,

        buildRendering: function () {
            this.inherited(arguments);

            this.municipalitySelectForm = new MunicipalitySelectForm({}, this.municipalitySelectForm);
            this.municipalitySelectForm.startup();
        },

        /**
         * 左側メニュー内の各画面のキー名とインスタンスの対応
         * @type {Object<string,ContentPane>}
         */
        _stackMap: null,

        /**
         * URL更新前のクエリーの内容
         * @type {Object}
         */
        _lastQuery: null,

        /**
         * 位置情報
         * @type {Object}
         */
        _latlng: null,

        /**
         * 地図のベースになるレイヤー
         * @type {String}
         */
        MUNICIPALITY_LAYER_URL: '/data/master/municipalities.geojson',

        constructor: function () {
            this._stackMap = {};
            this._lastQuery = {};
        },

        startup: function () {
            this.inherited(arguments);

            // 地図と隣り合う領域の境界(splitter）を非表示にしておく
            domStyle.set(this.detailPane._splitterWidget.domNode, 'display', 'none');
            this.wrapper.layout();

            // マップを初期化
            this.initMap();

            // 検索ボタン
            this.own(topic.subscribe('idis/view/form/GeocodingForm::geocoded', lang.hitch(this, function (payload) {
                this._latlng = payload.latlng;
                this.map.setView(this._latlng, 11);
            })));
            // 市町選択ボタン
            this.own(topic.subscribe('idis/view/form/MunicipalitySelectForm::selected',
                lang.hitch(this, function (payload) {
                    // 自動更新時は反応させない
                    if (payload.latlng) {
                        this._latlng = payload.latlng;
                        if (this.map) {
                            this.map.setView(this._latlng, payload.zoom);
                        }
                    }
                })));
            // 島しょ部移動
            this.own(topic.subscribe('app/map/islandPanel/IslandPanel::moveToIsland',
                lang.hitch(this, function (payload) {
                    this.map.setView(payload.latlng, payload.zoom);
                })));
            // 詳細表示領域の状態をURLに従って更新
            this.updateDetailPane();
            // URLの変更を監視
            this.own(Locator.on('change', lang.hitch(this, this.onLocationChanged)));
            // 中心位置表示用ボタンの状態をトグル
            if (!LocalStorage.hasKey || !LocalStorage.hasKey(STORAGE_KEY.CENTER_MARK)) {
                LocalStorage.set(STORAGE_KEY.CENTER_MARK, '1');
            }

            var centerMarkValue = !!LocalStorage.get(STORAGE_KEY.CENTER_MARK);
            domClass.toggle(this.centerMarkToggleButton, 'is-checked', centerMarkValue);
            // LocalStorageの変更を監視
            this.own(LocalStorage.watch(STORAGE_KEY.CENTER_MARK, function (value) {
                domClass.toggle(this.centerMarkToggleButton, 'is-checked', !!value);
            }, this));
            // 凡例
            this.own(topic.subscribe('app/monitor/ObservationPanel::showGridLegendLayer',
                lang.hitch(this, function () {
                    this.showGridLegendDialog();
                })));

            // 表示情報に対応するインスタンスを生成
            var key = 'layer';
            var pane = this._stackMap[key] = new stackClassMap[key]();
            this.stackPane.addChild(pane);

        },

        // ウィジェットを破棄する
        destroy: function () {
            this.inherited(arguments);
        },

        /**
         * マップ・オブジェクトにサイズ変更を通知する。
         */
        notifyResizeToMap: function () {
            // マップにサイズ変更を通知
            this.map.invalidateSize({ pan: false });
        },

        /**
         * URLが変更された際に反応する。
         */
        onLocationChanged: function () {
            // 現在のクエリー情報を取得
            var query = Locator.getQuery();
            // 詳細ペインの状態が変更された場合
            if (query[QUERY.DETAIL_LAYER_ID] !== this._lastQuery[QUERY.DETAIL_LAYER_ID]) {
                this.updateDetailPane();
            }
            // 次の更新に備えてクエリー状態を保存
            this._lastQuery = query;
        },

        /**
         * URLの状態に従い詳細領域を更新する。
         */
        updateDetailPane: function () {
            // URLから詳細表示用レイヤーIDを取得
            var layerId = Locator.getQuery()[QUERY.DETAIL_LAYER_ID];
            when(layerId && LayerStore.get(layerId), lang.hitch(this, function (item) {
                // 古いウィジェットを破棄
                array.forEach(this.detailContent.getChildren(), function (child) {
                    child.destroyRecursive();
                });
                // 表示対象が指定されているなら設置
                if (item) {
                    // 情報カテゴリー・コードに応じたウィジェットを取得
                    var widgetClass = DetailMap[item.infoCategoryCd];
                    // 詳細表示領域へ設置
                    this.detailTitle.innerHTML = item.name;
                    var widget = new widgetClass({ item: item });
                    this.detailContent.addChild(widget);
                }
                // 詳細表示領域の表示を調整
                this.detailPane.resize({ h: (item ? 250 : 0) });
                domStyle.set(this.detailPane._splitterWidget.domNode, 'display', item ? '' : 'none');
                // 再レイアウト
                this.wrapper.layout();
            }));
        },

        /**
         * 詳細領域を隠す。
         */
        hideDetailPane: function () {
            // URLから詳細レイヤーIDを削除する
            Locator.replaceState(QUERY.DETAIL_LAYER_ID, '');
        },

        /**
         * 凡例ダイアログを表示する。
         */
        toggleLegendDialog: function () {
            if (!this._legendDialog) {
                // 初回呼び出し時にインスタンス生成
                this._legendDialog = new IdisDialog({
                    noUnderlay: true,
                    title: '凡例',
                    content: new LegendPane()
                });
                // 画面が破棄された際に連れて行く
                this.own(this._legendDialog);
            }
            if (this._legendDialog.open) {
                this._legendDialog.hide();
            } else {
                this._legendDialog.show();
            }
        },

        showGridLegendDialog: function () {
            if (!this._gridLegendDialog) {
                // 初回呼び出し時にインスタンス生成
                this._gridLegendDialog = new IdisDialog({
                    noUnderlay: true,
                    title: '凡例',
                    content: new GridLegendPane()
                });
                // 画面が破棄された際に連れて行く
                this.own(this._gridLegendDialog);
            }
            if (this._gridLegendDialog.open) {
                this._gridLegendDialog.hide();
            } else {
                this._gridLegendDialog.show();
            }
        },

        /**
         * 背景地図ダイアログを表示する。
         */
        showBaseLayerDialog: function () {
            if (!this._baseLayerDialog) {
                // 初回呼び出し時にインスタンス生成
                this._baseLayerDialog = new IdisDialog({
                    noUnderlay: true,
                    title: '背景地図',
                    content: new BaseLayerPane({ map: this.map })
                });
                // 画面が破棄された際に連れて行く
                this.own(this._baseLayerDialog);
            }
            this._baseLayerDialog.show();
        },

        /**
         * 中心表示の状態を更新する。
         */
        toggleCenterMark: function () {
            // 設定値をトグル
            LocalStorage.set(STORAGE_KEY.CENTER_MARK, LocalStorage.get(STORAGE_KEY.CENTER_MARK) ? '' : '1');
        },

        showSubMenu: function () {
            domClass.add(this.container, 'is-subMenuShown');
        },

        hideSubMenu: function () {
            domClass.remove(this.container, 'is-subMenuShown');
        },

        initMap: function () {
            var municipalityCd = UserInfo.getSelectedMunicipalityCd();

            if (municipalityCd) {
                var self = this;
                Requester.get('/api/municipalities/' + municipalityCd, {
                    headers: { 'Content-Type': 'application/json; charset=utf-8' },
                    handleAs: 'json',
                    preventCache: true
                }).then(function (item) {

                    self._latlng = {
                        latitude: item.latitude,
                        longitude: item.longitude
                    };

                    // マップの初期化
                    self.map = new IdisMap(self.id + '-map', {
                        config: self._latlng,
                        keyboard: false, // コメント時に+/-が使用できないため
                        touchExtend: false, // IE対応
                        minZoom: 5,
                        maxZoom: 18,
                        drawControlTooltips: false
                    });
                    // mapのdestroyはremoveでDojoと互換なのでownで消せる
                    self.own(self.map);

                    // 最後のリサイズより10ミリ秒待ってからマップへ通知
                    var notifier = debounce(lang.hitch(self, 'notifyResizeToMap'), 10);
                    self.own(aspect.after(self.detailPane, 'resize', notifier));

                    // 行政界を表示
                    self.putFirstLayer();
                }, function (error) {
                    console.log(error);
                });
            } else {
                // デフォルトの位置情報を設定
                this._latlng = config.map;

                // マップの初期化
                this.map = new IdisMap(this.id + '-map', {
                    config: this._latlng,
                    keyboard: false, // コメント時に+/-が使用できないため
                    touchExtend: false, // IE対応
                    minZoom: 9,
                    maxZoom: 18,
                    drawControlTooltips: false
                });
                // mapのdestroyはremoveでDojoと互換なのでownで消せる
                this.own(this.map);

                // 最後のリサイズより10ミリ秒待ってからマップへ通知
                var notifier = debounce(lang.hitch(this, 'notifyResizeToMap'), 10);
                this.own(aspect.after(this.detailPane, 'resize', notifier));
            }
        },

        /**
         * 地図レイヤーの上に行政界レイヤーを敷く
         * 県の境が見にくいため、自治体ごとのpolygonを置いている.
         * layeridの'999'は仮
         * TODO: 将来的にURLで制御させるか検討要
         */
        putFirstLayer: function () {
            return this.map.layerControl.addGeneralLayer({
                'id': 999,
                'name': '行政界',
                'parentId': 0,
                'disasterId': 21,
                'dispSeqNo': 2,
                'infoCategoryCd': 'D108',
                'pubStatus': '9',
                'deptCd': 'D01001',
                'sectCd': 'S01003',
                'minZoom': 8,
                'tileFlg': false,
                'jsonType': '1',
                'listType': '0',
                'layerUrl': this.MUNICIPALITY_LAYER_URL,
                'styleUrl': 'style.js',
                'hideFlg': false,
                'userId': 'U01001'
            }, 1);
        },

    });
});
